import type { ReactNode } from 'react';

/**
 * English texts (default language). fr.tsx must provide exactly the same keys:
 * a missing or extra key fails `pnpm typecheck`.
 */
export const en = {
  meta: {
    description: 'Pizzeria Fornavia — authentic pizzas delivered with passion. Order online for delivery or pickup.',
    driverAppName: 'Fornavia Driver',
  },
  common: {
    language: 'Language',
    langNames: { en: 'English', fr: 'Français' },
    loading: 'Loading',
    retry: 'Try again',
    close: 'Close',
    error: 'Error',
    httpError: (status: number) => `Error ${status}`,
    cancel: 'Cancel',
    save: 'Save',
    add: 'Add',
    back: 'Back',
    signOut: 'Sign out',
    online: 'Online',
    offline: 'Offline',
    total: 'Total',
    subtotal: 'Subtotal',
    taxes: 'Taxes',
    tip: 'Tip',
    delivery: 'Delivery',
    pickup: 'Pickup',
    deliveryFee: 'Delivery fee',
    demoBadge: 'DEMO',
    system: 'System',
    items: (n: number) => `${n} item(s)`,
    order: (n: number | string) => `Order #${n}`,
    payment: 'Payment',
    refund: 'Refund',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal code',
    description: 'Description',
    closed: 'Closed',
    open: 'Open',
    closedToday: 'Closed today',
    percent: (n: number) => `${n}%`,
    customer: 'Customer',
    status: 'Status',
    type: 'Type',
    driver: 'Driver',
    driverLabel: 'Driver: ',
    viewMenu: 'View the menu',
    publicSite: 'Public site',
    required: 'Required',
    active: 'Active',
    disabled: 'Disabled',
    reconnecting: 'Reconnecting…',
  },
  app: {
    accessDenied: 'Access denied',
    noAccess: 'Your role does not give you access to this page.',
    switchAccount: 'Switch account',
  },
  install: {
    button: 'Install the app',
    short: 'Install',
    helpTitle: 'Add to Home Screen',
    iosIntro: 'On iPhone and iPad, you install the app from Safari:',
    iosStep1: (icon: ReactNode) => (
      <>
        Tap {icon} <b>Share</b> in the Safari toolbar.
      </>
    ),
    iosStep2: (icon: ReactNode) => (
      <>
        Choose {icon} <b>Add to Home Screen</b>.
      </>
    ),
    iosStep3: (appName: string) => (
      <>
        Tap <b>Add</b>: the {appName} icon appears with your other apps.
      </>
    ),
    otherIntro: 'Your browser has not offered automatic installation yet.',
    otherStep1: (
      <>
        Open the browser menu (<b>⋮</b> or <b>…</b>).
      </>
    ),
    otherStep2: (
      <>
        Choose <b>Install app</b> or <b>Add to Home screen</b>.
      </>
    ),
    otherNote: 'Chrome, Edge and Samsung Internet also show an install icon in the address bar. Installing requires an HTTPS connection.',
    gotIt: 'Got it',
    pageTitle: (name: string) => `Install ${name}`,
    perk1: 'Order in two taps from your home screen',
    perk2: 'Follow your driver live on the map',
    perk3: 'Order status updated automatically',
    alreadyInstalled: 'The app is already installed on this device.',
  },
  pub: {
    restaurantNotFound: 'Restaurant not found',
    navHome: 'Home',
    navMenu: 'Menu',
    navAbout: 'About',
    navContact: 'Contact us',
    myAccount: 'My account',
    signIn: 'Sign in',
    cart: 'Cart',
    openingHours: 'Opening hours',
    appTitle: 'App',
    appPitch: (name: string) => `Install ${name} on your phone to order in two taps.`,
    staffArea: 'Staff area',
    navigation: 'Navigation',
    orders: 'Orders',
    account: 'Account',
  },
  landing: {
    taglineFallback: 'Authentic. Fresh. Delicious.',
    orderMode: 'Order type',
    heroAlt: 'Pepperoni pizza',
    openNow: 'Open now',
    ordersAccepted: ' · orders accepted',
    deliveryEta: (min: number, max: number) => `Delivery ${min}–${max} min`,
    deliveryInfo: (fee: string, km: number) => `${fee} fee · ${km} km radius`,
    orderNow: 'Order now',
    ourCategories: 'Our categories',
    fullMenu: 'Full menu',
    favourites: 'Customer favourites',
    from: (price: string) => `From ${price}`,
    features: [
      { t: 'Handcrafted pizzas', d: 'House-made dough, stone oven.' },
      { t: 'Fresh ingredients', d: 'Local products every day.' },
      { t: 'Live tracking', d: 'See your driver on the map.' },
      { t: 'Open 7 days a week', d: '' },
    ],
    viewCart: (amount: string) => `View cart · ${amount}`,
  },
  menu: {
    categories: 'Categories',
    searchPlaceholder: 'Search for a dish…',
    search: 'Search',
    resultsFor: (q: string) => `Results for “${q}”`,
    ourCategory: (name: string | undefined) => `Our ${name?.toLowerCase() ?? 'dishes'}`,
    intro: 'Handcrafted recipes, fresh ingredients and dough made in-house every day.',
    allTags: 'All',
    noMatch: 'No dishes match.',
    fromPrefix: 'From ',
    addItem: (name: string) => `Add ${name}`,
    add: 'Add',
    stickyCart: (count: number, amount: string) => `Cart (${count}) · ${amount}`,
    yourOrder: 'Your order',
    yourOrderCount: (n: number) => `Your order (${n})`,
    clearAll: 'Clear all',
    emptyCart: 'Your cart is empty. Add a dish to get started.',
    estimatedTotal: 'Estimated total',
    checkout: 'Go to checkout',
    removeOne: 'Remove one',
    addOne: 'Add one',
  },
  product: {
    added: (name: string) => `${name} added to cart`,
    less: 'Decrease quantity',
    more: 'Increase quantity',
    addFor: 'Add — ',
    max: (n: number) => `Max ${n}`,
    optional: 'Optional',
    included: 'Included',
  },
  cart: {
    title: 'Your cart',
    empty: 'Your cart is empty.',
    taxesQc: 'Taxes (GST + QST)',
    minimum: (amount: string) => `Minimum order of ${amount}.`,
    keepShopping: 'Continue shopping',
  },
  checkout: {
    locationDenied: 'Location denied: place the pin on the map.',
    outOfZone: (km: string, max: number) => `Address outside the delivery area (${km} km, maximum ${max} km).`,
    networkError: 'Network error, please try again.',
    stepContact: 'Contact details',
    stepPayment: 'Payment',
    receptionMode: 'How would you like your order?',
    deliveryHint: 'We come to you',
    pickupHint: 'At the restaurant',
    deliveryAddress: 'Delivery address',
    addressPlaceholder: '1234 Rue des Lucioles',
    apartment: 'Apartment (optional)',
    instructions: 'Instructions (optional)',
    instructionsPlaceholder: 'Back door, door code…',
    pinPlaced: 'Pin placed on the map.',
    tapMap: 'Tap the map to mark your door exactly.',
    distance: (km: string) => `Distance: ${km} km`,
    myLocation: 'My location',
    outOfZoneAlert: (max: number) => `This address is outside our delivery area (${max} km).`,
    pickupAtRestaurant: 'Pickup at the restaurant',
    readyIn: (min: number) => `Ready in about ${min} minutes.`,
    fullName: 'Full name',
    kitchenNote: 'Note for the kitchen (optional)',
    kitchenNotePlaceholder: 'Allergies, preferences…',
    edit: 'Edit',
    tipTitle: 'Tip for your driver',
    noTip: 'None',
    demoNotice: 'Demo mode: no real card is charged. Test decline card: 4000 0000 0000 0002.',
    cardNumber: 'Card number',
    expiry: 'Expiry (MM/YY)',
    paying: 'Processing…',
    payNow: (total: string) => `Pay now · ${total}`,
    stripeReady: 'Payment ready for Stripe · secure transaction',
  },
  tracking: {
    steps: {
      PAID: { label: 'Order received', desc: 'Your order has been confirmed.' },
      PREPARING: { label: 'Preparing', desc: 'Our chefs are preparing your order.' },
      READY: { label: 'Ready', desc: 'Your order is ready.' },
      READY_PICKUP: 'Come pick it up at the counter.',
      ASSIGNED: { label: 'Driver assigned', desc: 'A driver has picked up your order.' },
      OUT_FOR_DELIVERY: { label: 'On the way', desc: 'Your driver is on their way.' },
      DELIVERED: { label: 'Delivered', desc: 'Enjoy your meal!' },
      PICKED_UP: { label: 'Picked up', desc: 'Thank you and enjoy your meal!' },
    },
    yourPlace: 'Your place',
    notFound: 'Order not found',
    placedAt: (time: string, restaurant: string) => `Placed at ${time} · ${restaurant}`,
    liveUpdates: 'Live updates',
    finished: 'Completed',
    live: 'Live',
    reconnect: 'Reconnecting',
    cancelledMsg: (refunded: boolean, phone: string) =>
      `This order was ${refunded ? 'refunded' : 'cancelled'}. Contact the restaurant at ${phone}.`,
    deliveredBy: (name: string) => `${name} delivered your order`,
    onTheWay: (name: string) => `${name} is on the way`,
    etaIn: 'Estimated arrival in',
    waiting: 'Waiting…',
    details: 'Details',
    totalPaid: 'Total paid',
    deliveryTo: (addr: string) => `Delivery: ${addr}`,
    callRestaurant: 'Call the restaurant',
    newOrder: 'New order',
    ended: 'Delivery complete: GPS tracking has stopped.',
    positionUpdated: (time: string) => `Location updated ${time}`,
    showNumber: (n: number) => `Show order number #${n} at the counter.`,
  },
  account: {
    hello: (name: string) => `Hi ${name}`,
    staffArea: 'Staff area',
    signIn: 'Sign in',
    createAccount: 'Create an account',
    passwordMin: '(8 characters minimum)',
    signInButton: 'Sign in',
    createButton: 'Create my account',
    demoAccount: 'Demo account: customer@fornavia.example / Demo123!',
    myOrders: 'My orders',
    track: 'Track',
    deviceOrders: 'Orders placed from this device:',
    orderFrom: (date: string) => `Order from ${date}`,
    noOrders: 'No orders yet.',
    appTitle: (name: string) => `The ${name} app`,
    appPitch: 'Order faster from your phone’s home screen.',
  },
  login: {
    signInError: 'Sign-in error',
    platformTitle: 'Delivery platform',
    platformPitch: 'Orders, kitchen, dispatch, drivers and finances in one place, in real time.',
    platformFooter: 'Many restaurants, one platform.',
    title: 'Sign in',
    subtitle: 'Staff, drivers and administrators',
    submit: 'Sign in',
    demoAccounts: 'DEMO accounts (password Demo123!):',
    backToSite: '← Back to the restaurant website',
  },
  bo: {
    nav: {
      dashboard: 'Dashboard',
      orders: 'Orders',
      kitchen: 'Kitchen (KDS)',
      dispatch: 'Dispatch',
      menu: 'Menu',
      customers: 'Customers',
      employees: 'Employees',
      finance: 'Finances',
      settings: 'Settings',
    },
    noRestaurant: 'No restaurant',
    noRestaurantText: 'This account is not an employee of any active restaurant.',
    menuToggle: 'Menu',
    restaurant: 'Restaurant',
  },
  dashboard: {
    vsYesterday: (p: number) => `${p >= 0 ? `+${p}` : p}% vs yesterday`,
    ordersToday: 'Orders today',
    netSales: 'Net sales',
    activeDrivers: 'Active drivers',
    outOf: (n: number) => `of ${n}`,
    avgDelivery: 'Average delivery time',
    last7: 'Last 7 days',
    salesByHour: 'Sales by hour (today)',
    hourLabel: (h: number) => `${h}:00`,
    quickActions: 'Quick actions',
    openKitchen: 'Open the kitchen screen',
    viewDrivers: 'View drivers',
    manageMenu: 'Manage the menu',
    salesReport: 'Sales report',
    newOrderSite: 'New order (website)',
    activeOrders: 'Active orders',
    allOrders: 'View all orders →',
    time: 'Time',
  },
  orders: {
    active: 'Active',
    history: 'History',
    all: 'All',
    allTypes: 'All types',
    searchPlaceholder: 'Customer, # or phone',
    items: 'Items',
    created: 'Created',
    none: 'No orders.',
    previous: 'Previous',
    next: 'Next',
    page: (p: number, n: number) => `Page ${p} / ${n}`,
  },
  drawer: {
    events: { CREATED: 'Created', PAID: 'Paid', UNASSIGNED: 'Driver removed' } as Record<string, string>,
    refunded: 'Refund completed',
    simulation: 'SIMULATION',
    note: (text: string) => `Note: ${text}`,
    driverQr: 'Driver QR',
    refund: 'Refund',
    cancel: 'Cancel',
    cancelTitle: (n: number) => `Cancel order #${n}?`,
    reasonPlaceholder: 'Reason (shown in the history)',
    keep: 'Keep',
    confirmCancel: 'Confirm cancellation',
    history: 'History',
    payments: 'Payments',
    qrCreated: (time: string) => `QR: created ${time}`,
    qrUsed: (time: string) => ` · used ${time}`,
    qrRevoked: ' · revoked',
    qrActive: ' · active',
  },
  actions: {
    accept: 'Accept',
    prepare: 'Start preparing',
    ready: 'Mark ready',
    handOver: 'Handed to customer',
    qrTitle: (n: number) => `Pickup QR · #${n}`,
    regenerate: 'Regenerate',
    print: 'Print receipt',
    orderLine: 'Order',
    qrAlt: (n: number) => `QR code for order ${n}`,
    qrHelp: (until: string | null) => `The driver scans this code in their app. Single-use code${until ? `, valid until ${until}` : ''}.`,
  },
  kitchen: {
    newOrder: (n: number) => `New order #${n}`,
    notifBody: (name: string, n: number) => `${name} · ${n} item(s)`,
    tabs: { all: 'All', new: 'Waiting', prep: 'Preparing', ready: 'Ready' },
    live: 'Live',
    sound: 'Sound',
    empty: 'No orders in this column.',
    showQr: 'Show QR code',
  },
  dispatch: {
    demoSuffix: ' · DEMO',
    assigned: 'Driver assigned',
    unassigned: 'Assignment removed',
    simStarted: (n: number) => `Simulation started for #${n}`,
    simStopped: 'Simulation stopped',
    filters: { all: 'All', prep: 'In the kitchen', ready: 'Ready', route: 'On the way' },
    none: 'No deliveries.',
    assignDriver: 'Assign a driver',
    notAssigned: 'Unassigned',
    available: '· available',
    offline: '· offline',
    seenAt: (time: string) => `· seen at ${time}`,
    waitingGps: '· waiting for GPS',
    stop: 'Stop',
    simulate: 'Simulate the route',
    onRoute: 'on the way',
    driversAvailable: 'drivers available',
    toPickUp: 'to pick up',
  },
  customers: {
    title: 'Customers',
    searchPlaceholder: 'Name, email, phone',
    contact: 'Contact',
    orders: 'Orders',
    spent: 'Total spent',
    lastOrder: 'Last order',
    account: 'Account',
    registered: 'Registered',
    guest: 'Guest',
    none: 'No customers.',
  },
  menuAdmin: {
    orderUpdated: 'Sort order updated',
    categories: 'Categories',
    moveUp: 'Move up',
    moveDown: 'Move down',
    categoryShown: 'Category visible',
    categoryHidden: 'Category hidden',
    visible: 'Visible',
    newCategory: 'New category',
    categoryAdded: 'Category added',
    products: 'Items',
    addProduct: 'Add an item',
    noProducts: 'No items in this category.',
    product: 'Item',
    basePrice: 'Base price',
    options: 'Options',
    available: 'Available',
    productAvailable: 'Item available',
    productOut: 'Item out of stock',
    editItem: (name: string) => `Edit ${name}`,
    imageUploaded: 'Image uploaded',
    saved: 'Item saved',
    newProduct: 'New item',
    basePriceField: 'Base price ($)',
    category: 'Category',
    availableToOrder: 'Available to order',
    changeImage: 'Change image',
    imageHint: 'PNG, JPG or WEBP, 3 MB maximum.',
    optionsTitle: 'Options and customizations',
    newGroupName: 'New option',
    choice: (n: number) => `Choice ${n}`,
    optionGroup: 'Option group',
    groupName: 'Group name',
    single: 'Single choice',
    multi: 'Multiple choices',
    deleteGroup: 'Delete group',
    option: 'Option',
    extraCharge: 'Extra charge',
    isDefault: 'default',
    remove: 'Remove',
    addChoice: 'Add a choice',
  },
  employees: {
    perms: {
      'dashboard.view': 'Dashboard',
      'orders.view': 'Orders',
      'kitchen.view': 'KDS',
      'dispatch.view': 'Dispatch',
      'menu.manage': 'Menu',
      'finance.view': 'Finances',
      'employees.manage': 'Employees',
      'settings.manage': 'Settings',
      'driver.deliver': 'Delivery',
    } as Record<string, string>,
    nowRole: (name: string, role: string) => `${name} is now ${role}`,
    accessDisabled: 'Access disabled',
    accessEnabled: 'Access re-enabled',
    title: (n: number) => `Employees (${n})`,
    add: 'Add an employee',
    employee: 'Employee',
    role: 'Role',
    permissions: 'Permissions',
    since: 'Since',
    access: 'Access',
    roleOf: (name: string) => `Role for ${name}`,
    disable: 'Disable',
    enable: 'Re-enable',
    audit: 'Audit log',
    added: 'Employee added',
    tempPassword: 'Temporary password (8 characters min.)',
  },
  finance: {
    periods: { today: 'Today', '7d': '7 days', '30d': '30 days', month: 'This month', custom: 'Custom' },
    from: 'From',
    to: 'To',
    exportCsv: 'Export CSV',
    netSales: 'Net sales',
    ordersAvg: (n: number, avg: string) => `${n} orders · avg. ${avg}`,
    taxes: 'Taxes collected',
    tips: 'Tips',
    deliveryFees: 'Delivery fees',
    refunded: (amount: string) => `− ${amount} refunded`,
    byDay: 'Revenue by day',
    breakdown: 'Revenue breakdown',
    food: 'Food',
    split: (d: number, p: number) => `${d} deliveries · ${p} pickups`,
    transactions: 'Transactions',
    fromDb: 'calculated from the database',
    date: 'Date',
    order: 'Order',
    movement: 'Movement',
    amount: 'Amount',
    paid: 'Paid',
    declined: 'Declined',
  },
  settings: {
    tabs: { info: 'Details', hours: 'Hours', zones: 'Delivery zones', modes: 'Service modes', payment: 'Payment', notifications: 'Notifications' },
    saved: 'Settings saved',
    imageUploaded: 'Image uploaded, remember to save',
    restaurantName: 'Restaurant name',
    logo: 'Logo',
    changeLogo: 'Change logo',
    heroImage: 'Home page image',
    changeImage: 'Change image',
    promoTitle: 'Home page promotion',
    showBanner: 'Show the banner',
    title: 'Title',
    subtitle: 'Subtitle',
    timezone: (tz: string) => `Time zone: ${tz}`,
    opening: (day: string) => `Opening time, ${day}`,
    closing: (day: string) => `Closing time, ${day}`,
    acceptWhenClosed: 'Accept orders outside opening hours (demo mode)',
    radius: 'Delivery radius (km)',
    fee: 'Delivery fee ($)',
    minimum: 'Minimum order ($)',
    mapHint: 'Tap the map to move the delivery starting point (the restaurant).',
    homeDelivery: 'Home delivery',
    pickupAtRestaurant: 'Pickup at the restaurant',
    prepTime: 'Average preparation time (min)',
    taxRate: 'Tax rate (%)',
    qrValidity: 'Pickup QR code validity (min)',
    activeProvider: 'Active provider: ',
    simulatedPayment: 'Simulated payment (demo)',
    stripeText: (a: ReactNode, b: ReactNode, c: ReactNode) => (
      <>
        The platform is ready for Stripe: to turn it on, the server administrator sets {a}, {b} and {c}. Until these variables are set, no call is
        made to Stripe.
      </>
    ),
    demoCards: 'Demo cards: 4242 4242 4242 4242 (approved), 4000 0000 0000 0002 (declined).',
    kitchenSound: 'Sound on the kitchen screen for every new order',
    sound: 'Sound',
    browserNotifs: 'Browser notifications for staff',
    browser: 'Browser',
    installTablet: 'Install the back office on this tablet:',
  },
  driver: {
    readyToast: 'An order is ready for pickup',
    nav: { home: 'Home', orders: 'Orders', history: 'History', profile: 'Profile' },
    toPickUpAria: (n: number) => `${n} to pick up`,
    nowOffline: 'You are offline',
    nowAvailable: 'You are available',
    hello: (name: string) => `Hi ${name}!`,
    available: 'Available',
    deliveriesToday: 'Deliveries today',
    earningsToday: 'Today’s earnings',
    gpsPrefix: 'GPS: ',
    simulationMode: 'Simulation mode',
    realGpsMode: 'Real GPS mode',
    deliveryInProgress: (n: number) => `Delivery in progress #${n}`,
    myOrders: 'My orders',
    history: 'History',
    profileLink: 'Profile and permissions',
    scanCta: 'Scan an order',
    assignedOrders: 'Assigned orders',
    toPickUp: (n: number) => `To pick up (${n})`,
    inProgress: (n: number) => `In progress (${n})`,
    noneReady: 'No orders ready right now.',
    noneActive: 'No delivery in progress.',
    assignedToYou: 'Assigned to you',
    scanToPickUp: 'Scan to pick up',
    viewDelivery: 'View delivery',
    historyTotal: (n: number, amount: string) => `${n} deliveries · ${amount}`,
    noHistory: 'No completed deliveries.',
    earningsHint: 'delivery + tip',
    perm: { granted: 'Allowed', denied: 'Denied', prompt: 'Not asked yet', unknown: 'Unknown' },
    roleAt: (restaurant: string) => `Driver · ${restaurant}`,
    permissions: 'Permissions',
    location: 'Location: ',
    camera: 'Camera: ',
    allow: 'Allow',
    permHelp: 'If a permission is denied, turn it back on in your browser settings for this site (padlock icon).',
    locationSource: 'Location source',
    realGps: 'Real GPS',
    simulation: 'Simulation (demo)',
    simulationHelp: 'The simulation moves the driver from the restaurant to the customer, for a demo without driving.',
    gps: {
      idle: 'GPS on standby',
      requesting: 'Requesting GPS…',
      active: 'GPS active',
      denied: 'GPS denied',
      unavailable: 'GPS unavailable',
      error: 'Weak GPS signal',
    },
    sendFailed: 'Could not send location',
    networkError: 'Network error',
    cameraDenied: 'Camera access denied. Allow the camera in your browser settings, or use a photo.',
    cameraUnavailable: 'Camera unavailable on this device. Use a photo of the QR code.',
    noQrInPhoto: 'No QR code found in the photo. Try again closer to the code.',
    scanTitle: 'Scan the order QR code',
    pickedUp: (n: number) => `Order #${n} picked up`,
    pickedUpText: (name: ReactNode) => (
      <>
        Delivery for <b>{name}</b>. GPS tracking starts and the customer can see your location.
      </>
    ),
    startDelivery: 'Start the delivery',
    cameraAria: 'Camera',
    frameHint: 'Place the QR code inside the frame',
    checking: 'Checking…',
    searching: 'Looking for the QR code…',
    openCamera: 'Open the camera',
    takePhoto: 'Take a photo of the QR code',
    enterCode: 'Enter the code',
    codePlaceholder: 'QR code or link',
    submit: 'Submit',
    deliveryNotFound: 'Delivery not found',
    delivered: (n: number) => `Order #${n} delivered`,
    deliveredBadge: 'Delivered',
    outForDelivery: 'Out for delivery',
    arrival: (n: number) => `Arrival ≈ ${n} min`,
    gpsDenied: 'Location is denied: the customer cannot see your route. Allow location in the browser, or switch to simulation mode in your profile.',
    restartSim: 'Restart the simulation',
    callCustomer: 'Call the customer',
    call: 'Call',
    paid: '(paid)',
    openMaps: 'Open in Maps',
    finish: 'Complete the delivery',
    confirmTitle: 'Confirm delivery',
    handedOver: 'Order handed to the customer',
    confirmText: (n: number, name: string) => `Confirm that order #${n} was handed to ${name}. GPS tracking stops for the customer.`,
  },
  platform: {
    superAdmin: 'Platform super admin',
    restaurants: 'Restaurants',
    systemStatus: 'System status',
    activated: (name: string) => `${name} activated`,
    deactivated: (name: string) => `${name} deactivated`,
    restaurantsCount: (n: number) => `Restaurants (${n})`,
    restaurant: 'Restaurant',
    orders: 'Orders',
    active: 'Active',
    inProgress: 'In progress',
    netSales: 'Net sales',
    employees: 'Employees',
    activate: (name: string) => `Activate ${name}`,
    open: 'Open',
    created: (date: string) => `created ${date}`,
    deactivatedBadge: 'Deactivated',
    deactivate: 'Deactivate',
    activateBtn: 'Activate',
    menuItems: 'Menu items',
    team: 'Team',
    ordersByStatus: 'Orders by status',
    recentAudit: 'Recent audit',
    rows: {
      api: 'API',
      operational: 'Operational',
      started: 'Started',
      database: 'Database',
      realtime: 'Realtime connections',
      payment: 'Payment',
      paymentMock: 'Simulated (demo) — Stripe ready, not connected',
      driverPosition: 'Driver location',
      realGps: 'Real GPS (+ simulation available)',
      demo: 'Demo',
      simulator: 'Simulator',
      simulatorOn: (n: number) => `On · ${n} route(s) running`,
      simulatorOff: 'Off',
    },
    refresh: 'Refresh',
    data: 'Data',
    migrations: 'Migrations',
    platformAudit: 'Platform audit',
  },
};

export type Messages = typeof en;
